import { throttle } from 'lodash-es';
import { useEffect, useState, useRef } from 'react';

const MOBILE_BREAKPOINT = 768; // 모바일 화면 너비 기준 (768px)
const SCROLL_THROTTLE_TIME = 200; // 스크롤 이벤트 제한 시간 (ms)

/**
 * 모바일 환경에서 스크롤 방향에 따라 UI의 가시성을 제어하는 React 훅.
 *
 * - 아래로 스크롤하면 `false` (숨김), 위로 스크롤하면 `true` (보임).
 * - 데스크탑 환경에서는 항상 `true`를 반환.
 * - `throttle`을 적용하여 스크롤 이벤트의 호출 빈도를 제한하여 성능 최적화.
 *
 * @returns boolean - 모바일 환경에서 헤더가 보여야 하는지 여부 (`true`: 보임, `false`: 숨김)
 */
const useScrollVisibility = (isScrollUpdating = false) => {
  // 스크롤 방향에 따라 헤더 표시 여부 관리 (true: 보임, false: 숨김)
  const [isVisible, setIsVisible] = useState(true);

  // 현재 화면이 모바일인지 여부를 상태로 관리
  const [isMobileWidth, setIsMobileWidth] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT,
  );

  // 마지막 스크롤 위치를 저장하는 ref (리렌더링을 방지하기 위함)
  const lastScrollY = useRef<number>();

  // 📌 화면 크기가 변경될 때 모바일 여부를 업데이트하는 핸들러
  const handleResize = () => setIsMobileWidth(window.innerWidth < MOBILE_BREAKPOINT);

  const isScrollUpdatingRef = useRef(isScrollUpdating);

  useEffect(() => {
    isScrollUpdatingRef.current = isScrollUpdating;
  }, [isScrollUpdating]);

  // 📌 스크롤 이벤트 핸들러
  const handleScroll = throttle(
    () => {
      const currentScrollY = window.scrollY;

      // 아래로 스크롤하면 숨김, 위로 스크롤하면 표시
      if (lastScrollY.current !== undefined) {
        if (currentScrollY < 5 || isScrollUpdatingRef.current) {
          setIsVisible(true);
        } else {
          setIsVisible(currentScrollY < lastScrollY.current);
        }
      }

      // 현재 스크롤 위치 업데이트
      lastScrollY.current = currentScrollY;
    },
    SCROLL_THROTTLE_TIME,
    { leading: false, trailing: true },
  );

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // 📌 모바일 환경에서만 `isVisible` 상태를 반환, 데스크탑에서는 항상 `true`
  return isMobileWidth ? isVisible : true;
};

export default useScrollVisibility;
